import { useEffect, useState } from "react";
import { IoMdCloseCircle } from "react-icons/io";
import { useNavigate } from "react-router-dom";
import formatValues from "../../../../scripts/formatValues.js";
import { getCommissionedCompanies } from "../../services/getCommissionedCompanies.js";
import { unlinkCommissionedCompany } from "../../services/unlinkCommissionedCompany.js";
import { updateCommissionedCompanyValue } from "../../services/updateCommissionedCompanyValue.js";
import LinkCompanyModal from "../linkCompanyModal/index.jsx";
import styles from "./css/companiesLinkModal.module.css";


// CALCULA O "SELO" DE STATUS DO VÍNCULO EM RELAÇÃO AO TOTAL DO MÊS CORRENTE,
// REPLICANDO NO CLIENT A MESMA REGRA JÁ APLICADA NO SERVIDOR (SEÇÃO 3.3 DA SPEC).
function getPaymentBadge({ nextPayment, companyDeleted }) {
    if (companyDeleted) {
        return { label: "Empresa excluída — não conta no total", tone: "neutral" };
    }

    if (!nextPayment) {
        return { label: "Sem previsão de cobrança", tone: "neutral" };
    }

    const paymentDate = new Date(nextPayment);
    const paymentMonthIndex = paymentDate.getUTCFullYear() * 12 + paymentDate.getUTCMonth();

    const today = new Date();
    const todayMonthIndex = today.getFullYear() * 12 + today.getMonth();

    const monthLabel = paymentDate.toLocaleDateString('pt-BR', { month: '2-digit', year: 'numeric', timeZone: 'UTC' });

    if (paymentMonthIndex === todayMonthIndex) {
        return { label: "Conta neste mês", tone: "success" };
    }

    if (paymentMonthIndex < todayMonthIndex) {
        return { label: `Atrasado desde ${monthLabel}`, tone: "alert" };
    }

    return { label: `Só a partir de ${monthLabel}`, tone: "neutral" };
}


// STATUS DE OPERAÇÃO DA EMPRESA (companies.company.operating) — MESMA NOMENCLATURA/CORES
// JÁ USADAS EM admin/src/pages/clients/components/clientsList/index.jsx (getCompanyInsights).
function getOperatingBadge(operating) {
    return operating
        ? { label: "Válida", tone: "success" }
        : { label: "Em espera", tone: "warning" };
}


export default function CompaniesLinkModal({ show, agent, onClose, onDataChanged }) {
    const navigate = useNavigate();

    const [companies, setCompanies] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [showLinkModal, setShowLinkModal] = useState(false);

    const [editingMapId, setEditingMapId] = useState(null);
    const [editingValue, setEditingValue] = useState("");
    const [rowError, setRowError] = useState({ id: null, message: null });
    const [rowLoadingId, setRowLoadingId] = useState(null);


    useEffect(() => {
        const handleGetCompanies = async () => {
            try {
                if (!show || !agent?.id) return;

                setLoading(true);
                setError(null);

                const data = await getCommissionedCompanies({ commissionedId: agent.id });
                setCompanies(data);

            } catch (err) {
                console.error("ERROR - GET COMMISSIONED COMPANIES FUNCTION:", err);
                setCompanies([]);
                setError("Não foi possível carregar as empresas vinculadas no momento.");

            } finally {
                setLoading(false);
            }
        };

        handleGetCompanies();

        setShowLinkModal(false);
    }, [show, agent]);


    if (!show) return null;


    const handleReloadCompanies = async () => {
        const data = await getCommissionedCompanies({ commissionedId: agent.id });
        setCompanies(data);

        if (onDataChanged) onDataChanged();
    };


    const handleLinked = async () => {
        setShowLinkModal(false);
        await handleReloadCompanies();
    };


    const handleStartEdit = (row) => {
        setEditingMapId(row.id);
        setEditingValue(String(row.commissionValue));
        setRowError({ id: null, message: null });
    };


    const handleSaveEdit = async (row) => {
        try {
            setRowLoadingId(row.id);
            setRowError({ id: null, message: null });

            await updateCommissionedCompanyValue({ mapId: row.id, commissionValue: Number(editingValue) });

            setEditingMapId(null);
            setEditingValue("");

            await handleReloadCompanies();

        } catch (err) {
            console.error("ERROR - UPDATE COMMISSIONED COMPANY VALUE FUNCTION:", err);
            setRowError({ id: row.id, message: err?.response?.data?.error || "Não foi possível atualizar o valor no momento." });

        } finally {
            setRowLoadingId(null);
        }
    };


    const handleUnlink = async (row) => {
        try {
            setRowLoadingId(row.id);
            setRowError({ id: null, message: null });

            await unlinkCommissionedCompany({ mapId: row.id });

            await handleReloadCompanies();

        } catch (err) {
            console.error("ERROR - UNLINK COMMISSIONED COMPANY FUNCTION:", err);
            setRowError({ id: row.id, message: err?.response?.data?.error || "Não foi possível desvincular a empresa no momento." });

        } finally {
            setRowLoadingId(null);
        }
    };


    const handleOpenCompanyDetails = (companyId) => {
        onClose();
        navigate(`/clients/${companyId}`);
    };


    const activeCompanies = companies.filter((item) => !item.unlinkedAt);
    const historyCompanies = companies.filter((item) => item.unlinkedAt);

    const badgeClassName = (tone) => {
        if (tone === "success") return `${styles.badge} ${styles.badgeSuccess}`;
        if (tone === "alert") return `${styles.badge} ${styles.badgeAlert}`;
        if (tone === "warning") return `${styles.badge} ${styles.badgeWarning}`;
        return `${styles.badge} ${styles.badgeNeutral}`;
    };


    return (
        <>
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modalContainer} onClick={(event) => event.stopPropagation()}>
                <header className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Empresas de {agent?.name || "comissionado"}</h2>

                    <button type="button" className={styles.closeButton} onClick={onClose}>
                        <IoMdCloseCircle size={24} color="#2f3e49" />
                    </button>
                </header>

                <div className={styles.modalBody}>

                    {!agent?.status && (
                        <div className={styles.inactiveAgentBanner}>
                            Comissionado inativo — vínculos ativos não entram no total do mês até ele ser reativado.
                        </div>
                    )}

                    <div className={styles.linkSection}>
                        <button
                            type="button"
                            className={styles.smallButton}
                            onClick={() => setShowLinkModal(true)}
                        >
                            Vincular empresa
                        </button>
                    </div>

                    {loading ? (
                        <p className={styles.feedbackText}>Carregando empresas vinculadas...</p>

                    ) : error ? (
                        <p className={styles.feedbackText}>{error}</p>

                    ) : companies.length === 0 ? (
                        <p className={styles.feedbackText}>Nenhuma empresa vinculada a este comissionado ainda.</p>

                    ) : (
                        <>
                            <p className={styles.sectionTitle}>Empresas vinculadas</p>

                            {activeCompanies.length === 0 ? (
                                <p className={styles.feedbackText}>Nenhuma empresa vinculada ativa no momento.</p>

                            ) : (
                                <div className={styles.listArea}>
                                    {activeCompanies.map((row) => {
                                        const badge = getPaymentBadge({ nextPayment: row.nextPayment, companyDeleted: row.companyDeleted });
                                        const operatingBadge = getOperatingBadge(row.companyOperating);

                                        return (
                                            <article key={row.id} className={styles.bindingCard}>
                                                <div className={styles.bindingCardHeader}>
                                                    <p className={styles.companyName}>{row.companyName}</p>

                                                    <div className={styles.badgeRow}>
                                                        <span className={badgeClassName(operatingBadge.tone)}>{operatingBadge.label}</span>
                                                        {row.companyDeleted && <span className={badgeClassName("alert")}>Excluída</span>}
                                                        <span className={badgeClassName(badge.tone)}>{badge.label}</span>
                                                    </div>
                                                </div>

                                                <p className={styles.linkedAtText}>
                                                    Mensalidade atual: {row.monthlyCost ? formatValues(row.monthlyCost) : 'não configurada'} · Vinculado em {new Date(row.linkedAt).toLocaleDateString('pt-BR')}
                                                </p>

                                                <div className={styles.linkFormRow}>
                                                    <button
                                                        type="button"
                                                        className={styles.smallButton}
                                                        onClick={() => handleOpenCompanyDetails(row.companyId)}
                                                    >
                                                        Abrir empresa
                                                    </button>
                                                </div>

                                                {editingMapId === row.id ? (
                                                    <div className={styles.linkFormRow}>
                                                        <input
                                                            className={styles.valueInput}
                                                            type="number"
                                                            min={0}
                                                            max={row.monthlyCost || undefined}
                                                            step="0.01"
                                                            value={editingValue}
                                                            onChange={(event) => setEditingValue(event.target.value)}
                                                        />

                                                        <button
                                                            type="button"
                                                            className={styles.smallButton}
                                                            disabled={rowLoadingId === row.id || editingValue === "" || Number(editingValue) < 0 || (row.monthlyCost > 0 && Number(editingValue) > row.monthlyCost)}
                                                            onClick={() => handleSaveEdit(row)}
                                                        >
                                                            {rowLoadingId === row.id ? 'Salvando...' : 'Salvar'}
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className={styles.dangerButton}
                                                            onClick={() => { setEditingMapId(null); setEditingValue(""); }}
                                                        >
                                                            Cancelar
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <div className={styles.linkFormRow}>
                                                        <span className={styles.availableItemName}>Comissão: {formatValues(row.commissionValue)}</span>

                                                        <button
                                                            type="button"
                                                            className={styles.smallButton}
                                                            onClick={() => handleStartEdit(row)}
                                                        >
                                                            Editar valor
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className={styles.dangerButton}
                                                            disabled={rowLoadingId === row.id}
                                                            onClick={() => handleUnlink(row)}
                                                        >
                                                            {rowLoadingId === row.id ? 'Desvinculando...' : 'Desvincular'}
                                                        </button>
                                                    </div>
                                                )}

                                                {rowError.id === row.id && (
                                                    <p className={styles.inlineError}>{rowError.message}</p>
                                                )}
                                            </article>
                                        );
                                    })}
                                </div>
                            )}

                            {historyCompanies.length > 0 && (
                                <>
                                    <p className={styles.historyDivider}>Histórico de vínculos encerrados</p>

                                    <div className={styles.listArea}>
                                        {historyCompanies.map((row) => {
                                            const operatingBadge = getOperatingBadge(row.companyOperating);

                                            return (
                                                <article key={row.id} className={`${styles.bindingCard} ${styles.unlinkedCard}`}>
                                                    <div className={styles.bindingCardHeader}>
                                                        <p className={styles.companyName}>{row.companyName}</p>

                                                        <div className={styles.badgeRow}>
                                                            <span className={badgeClassName(operatingBadge.tone)}>{operatingBadge.label}</span>
                                                            {row.companyDeleted && <span className={badgeClassName("alert")}>Excluída</span>}
                                                            <span className={styles.availableItemName}>{formatValues(row.commissionValue)}</span>
                                                        </div>
                                                    </div>

                                                    <p className={styles.linkedAtText}>
                                                        Vinculado em {new Date(row.linkedAt).toLocaleDateString('pt-BR')} · Desvinculado em {new Date(row.unlinkedAt).toLocaleDateString('pt-BR')}
                                                    </p>

                                                    <div className={styles.linkFormRow}>
                                                        <button
                                                            type="button"
                                                            className={styles.smallButton}
                                                            onClick={() => handleOpenCompanyDetails(row.companyId)}
                                                        >
                                                            Abrir empresa
                                                        </button>
                                                    </div>
                                                </article>
                                            );
                                        })}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>

        <LinkCompanyModal
            show={showLinkModal}
            agent={agent}
            onClose={() => setShowLinkModal(false)}
            onLinked={handleLinked}
        />
        </>
    );
}
